import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  Typography,
  Grid,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import { ArrowBack, Download, BarChart, TrendingUp } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { reportService } from '../services/api';

const ReportDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });

  useEffect(() => {
    fetchReportDetail();
  }, [id]);

  const fetchReportDetail = async () => {
    try {
      setLoading(true);
      const response = await reportService.getReportById(id);
      setReport(response.data.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      setToast({ open: true, message: 'Error loading report', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!report) return;

    const csvContent = [
      ['Report Details'],
      ['Campaign', report.campaign_title || 'N/A'],
      ['Report Type', report.report_type],
      ['Report Date', report.report_date],
      [''],
      ['Key Metrics'],
      ['Total Donations', `₹${report.total_donations?.toLocaleString() || 0}`],
      ['Donor Count', report.donor_count],
      ['New Donors', report.new_donors],
      ['Average Donation', `₹${report.average_donation?.toLocaleString() || 0}`],
      ['Recurring Donors', report.recurring_donors],
      ['Completion %', `${report.completion_percentage}%`],
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${id}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!report) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">Report not found</Typography>
        <Button onClick={() => navigate('/admin/reports')} sx={{ mt: 2 }}>
          Back to Reports
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f8fafb', minHeight: '100vh', py: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/reports')}
            sx={{ color: '#2c5f2d', mb: 2 }}
          >
            Back to Reports
          </Button>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 1 }}>
                {report.campaign_title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085' }}>
                Report for {new Date(report.report_date).toLocaleDateString()} ({report.report_type})
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleExport}
              sx={{
                backgroundColor: '#2c5f2d',
                '&:hover': { backgroundColor: '#1e4620' },
              }}
            >
              Export Report
            </Button>
          </Box>
        </Box>

        {/* Campaign Overview */}
        <Card sx={{ p: 4, mb: 4, border: '1px solid #e8ecf1', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 3 }}>
            Campaign Overview
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: '#667085', mb: 1 }}>
                Campaign Description
              </Typography>
              <Typography sx={{ color: '#1a202c', lineHeight: 1.6 }}>
                {report.campaign_description || 'No description available'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#667085', mb: 0.5 }}>
                    Status
                  </Typography>
                  <Typography sx={{ fontWeight: '600', color: '#2c5f2d', textTransform: 'capitalize' }}>
                    {report.campaign_status}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#667085', mb: 0.5 }}>
                    Target Amount
                  </Typography>
                  <Typography sx={{ fontWeight: '600', color: '#1a202c' }}>
                    ₹{report.target_amount?.toLocaleString() || 0}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Progress Bar */}
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: '600', color: '#1a202c' }}>
                Campaign Progress
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: '600', color: '#2c5f2d' }}>
                {(parseFloat(report.completion_percentage) || 0).toFixed(1)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={parseFloat(report.completion_percentage) || 0}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#e5e7eb',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#2c5f2d',
                  borderRadius: 4,
                },
              }}
            />
          </Box>
        </Card>

        {/* Key Metrics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 3, border: '1px solid #e8ecf1' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ backgroundColor: '#bfdbfe', p: 2, borderRadius: '12px' }}>
                  <BarChart sx={{ color: '#1e40af', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085' }}>
                    Total Donations
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e40af' }}>
                    ₹{parseFloat(report.total_donations || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 3, border: '1px solid #e8ecf1' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ backgroundColor: '#dcfce7', p: 2, borderRadius: '12px' }}>
                  <TrendingUp sx={{ color: '#2c5f2d', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085' }}>
                    Total Donors
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c5f2d' }}>
                    {report.donor_count?.toLocaleString() || 0}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 3, border: '1px solid #e8ecf1' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ backgroundColor: '#fed7aa', p: 2, borderRadius: '12px' }}>
                  <BarChart sx={{ color: '#b45309', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085' }}>
                    Avg Donation
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#b45309' }}>
                    ₹{parseFloat(report.average_donation || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 3, border: '1px solid #e8ecf1' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ backgroundColor: '#fecdd3', p: 2, borderRadius: '12px' }}>
                  <BarChart sx={{ color: '#dc2626', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085' }}>
                    New Donors
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#dc2626' }}>
                    {report.new_donors || 0}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Detailed Metrics Table */}
        <Card sx={{ border: '1px solid #e8ecf1', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 3 }}>
              Detailed Metrics
            </Typography>
          </Box>

          <Table>
            <TableBody>
              <TableRow sx={{ borderBottom: '1px solid #e8ecf1' }}>
                <TableCell sx={{ fontWeight: '600', color: '#1a202c', py: 2 }}>
                  Total Donations
                </TableCell>
                <TableCell sx={{ color: '#667085', textAlign: 'right' }}>
                  ₹{parseFloat(report.total_donations || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </TableCell>
              </TableRow>
              <TableRow sx={{ borderBottom: '1px solid #e8ecf1' }}>
                <TableCell sx={{ fontWeight: '600', color: '#1a202c', py: 2 }}>
                  Total Donors
                </TableCell>
                <TableCell sx={{ color: '#667085', textAlign: 'right' }}>
                  {report.donor_count || 0}
                </TableCell>
              </TableRow>
              <TableRow sx={{ borderBottom: '1px solid #e8ecf1' }}>
                <TableCell sx={{ fontWeight: '600', color: '#1a202c', py: 2 }}>
                  New Donors
                </TableCell>
                <TableCell sx={{ color: '#667085', textAlign: 'right' }}>
                  {report.new_donors || 0}
                </TableCell>
              </TableRow>
              <TableRow sx={{ borderBottom: '1px solid #e8ecf1' }}>
                <TableCell sx={{ fontWeight: '600', color: '#1a202c', py: 2 }}>
                  Recurring Donors
                </TableCell>
                <TableCell sx={{ color: '#667085', textAlign: 'right' }}>
                  {report.recurring_donors || 0}
                </TableCell>
              </TableRow>
              <TableRow sx={{ borderBottom: '1px solid #e8ecf1' }}>
                <TableCell sx={{ fontWeight: '600', color: '#1a202c', py: 2 }}>
                  Average Donation
                </TableCell>
                <TableCell sx={{ color: '#667085', textAlign: 'right' }}>
                  ₹{parseFloat(report.average_donation || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: '600', color: '#1a202c', py: 2 }}>
                  Campaign Completion
                </TableCell>
                <TableCell sx={{ color: '#667085', textAlign: 'right' }}>
                  {(parseFloat(report.completion_percentage) || 0).toFixed(2)}%
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Card>
      </Container>

      {/* Toast */}
      <Snackbar open={toast.open} autoHideDuration={6000} onClose={() => setToast({ ...toast, open: false })}>
        <Alert severity={toast.type} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportDetailPage;
